/**
 * BindWallet — bind EVM withdrawal / wallet login address on php_user_user.address.
 *
 * POST /api/users/bind-wallet  { wallet_address, transaction_password, security_answer }
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { useWalletAuthLogin } from '../wallet/useWalletAuthLogin';
import { Button } from '../components/ui/Button';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedBottomActionBar, ThemedInput, ThemedField, ThemedNotice, ThemedPanel } from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { CASHFLOW_COLORS as C } from '@/constants';

interface BindWalletPayload {
  wallet_address: string;
  transaction_password: string;
  security_answer: string;
}

function shortAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export function BindWallet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const wallet = useWalletAuthLogin();
  const { address: connectedAddress, isConnected } = useAccount();

  const [walletAddress, setWalletAddress] = useState(connectedAddress ?? '');
  const autoAddressRef = useRef('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurityAnswer, setShowSecurityAnswer] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!connectedAddress) return;
    setWalletAddress((current) =>
      current === '' || current === autoAddressRef.current ? connectedAddress : current,
    );
    autoAddressRef.current = connectedAddress;
  }, [connectedAddress]);

  const { data: securityStatus, isLoading: securityLoading } = useQuery({
    queryKey: ['users', 'security-status'],
    queryFn: () => usersAPI.getSecurityStatus(),
  });

  const bind = useMutation({
    mutationFn: (payload: BindWalletPayload) => usersAPI.bindWallet(payload),
    onSuccess: () => {
      setSuccessMsg(t('bindWallet.successMsg'));
      setErrorMsg('');
      void qc.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.message : t('bindWallet.bindFailed'));
      setSuccessMsg('');
    },
  });

  const securityReady = Boolean(
    securityStatus?.transaction_password_set && securityStatus?.security_question,
  );

  const canSubmit =
    walletAddress.trim() !== '' &&
    txPassword.trim() !== '' &&
    securityAnswer.trim() !== '' &&
    securityReady &&
    !successMsg &&
    !bind.isPending &&
    !securityLoading;

  function handleSubmit() {
    setErrorMsg('');
    setSuccessMsg('');

    if (!walletAddress.trim()) {
      setErrorMsg(t('bindWallet.enterAddress'));
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress.trim())) {
      setErrorMsg(t('bindWallet.invalidEvmAddress'));
      return;
    }
    if (!txPassword.trim()) {
      setErrorMsg(t('common.enterTxPassword'));
      return;
    }
    if (!securityAnswer.trim()) {
      setErrorMsg(t('common.enterSecurityAnswer'));
      return;
    }
    if (!securityReady) {
      setErrorMsg(t('bindWallet.setupSecurityFirst'));
      return;
    }

    bind.mutate({
      wallet_address: walletAddress.trim(),
      transaction_password: txPassword,
      security_answer: securityAnswer.trim(),
    });
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('bindWallet.title')} onBack={() => navigate(-1)} theme={C} />

        <div className="flex flex-col gap-5 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 88px)', paddingBottom: '112px' }}>
          <ThemedNotice tone="warning">{t('bindWallet.warning')}</ThemedNotice>
          <ThemedNotice tone="info">{t('bindWallet.connectThenBindHint')}</ThemedNotice>

          {!securityLoading && !securityReady ? (
            <ThemedNotice tone="error">
              {t('bindWallet.setupSecurityFirst')}{' '}
              <button type="button" className="underline" onClick={() => navigate('/secure')}>
                {t('bindWallet.goSecureSettings')}
              </button>
            </ThemedNotice>
          ) : null}

          <ThemedPanel className="p-4" theme={C}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  fullWidth
                  disabled={!wallet.isConfigured}
                  onClick={() => void wallet.openWalletModal()}
                  className="rounded-xl border text-sm font-medium hover:brightness-110 disabled:brightness-75"
                  style={{
                    background: C.buttonBg,
                    borderColor: C.buttonBorder,
                    boxShadow: C.buttonShadow,
                    color: C.text,
                  }}
                >
                  {isConnected && connectedAddress
                    ? t('bindWallet.connectedAs', { address: shortAddr(connectedAddress) })
                    : t('auth.connectWallet')}
                </Button>
                {!wallet.isConfigured ? (
                  <p className="text-xs" style={{ color: C.muted }}>
                    {t('rechargeTp.walletNotConfigured')}
                  </p>
                ) : null}
              </div>

              <ThemedField label={t('bindWallet.address')} required>
                <ThemedInput
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder={t('bindWallet.addressPlaceholder')}
                  autoComplete="off"
                  spellCheck={false}
                />
              </ThemedField>

              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurityAnswer}
                onShowSecurityChange={setShowSecurityAnswer}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityStatus?.security_question ?? undefined}
                colors={{
                  muted: C.muted,
                  red: C.red,
                  activeGreen: C.activeGreen,
                  gold: C.gold,
                  softBorder: C.softBorder,
                  inputBg: C.inputBg,
                }}
              />
            </div>
          </ThemedPanel>

          {errorMsg && <ThemedNotice tone="error">{errorMsg}</ThemedNotice>}
          {successMsg && <ThemedNotice tone="success">{successMsg}</ThemedNotice>}
        </div>

        <ThemedBottomActionBar background={C.bottomBarBg} className="pb-5 pt-3">
          <Button
            type="button"
            fullWidth
            isLoading={bind.isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="rounded-xl border text-sm font-medium hover:brightness-110 disabled:brightness-75"
            style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}
          >
            {t('bindWallet.submit')}
          </Button>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export default BindWallet;
